import React, { useCallback, useRef, useState, useMemo } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useNotifications } from '../../../../notifications';
import { SendReminderConfirmationModal } from './SendReminderConfirmationModal';
import { useDocumentState } from '../../../../contexts/DocumentContext';
import { remindCurrentSigner } from '../../../../utils/api/remind-current-signer';

import { getDocumentSigners } from '../../../../api/queries_v2';

const UNSIGN_DOCUMENT_ERROR_MESSAGE = "Unable to remove the signature.";

export const SendReminderConfirmationModalContainer = ({
  show,
  onHide,
  dealId: _dealId,
  teamId: _teamId,
  documentId,
}: {
  show: boolean;
  onHide: any;
  teamId: string | null;
  dealId: string;
  documentId?: string;
}) => {
  const [isSending, setIsSending] = useState<boolean>(false);
  const optionalMessageInputRef = useRef<HTMLTextAreaElement>(null);
  const apolloClient = useApolloClient();

  const { state } = useDocumentState();
  const currentDocumentId = state.id;

  const { data } = useQuery(getDocumentSigners, {
    variables: { documentId: documentId! },
    skip: !documentId,
  });

  const documentSigners = state.signers;

  const signers = documentId ? data?.getDocumentById.signers : documentSigners;

  const [, { error: notifyError, success: notifySuccess }] = useNotifications();

  const nextSigner = useMemo(() => {
    if (!signers) {
      return null;
    }
    const nextSigners = [...signers].filter((signer) => !signer.signedAt);
    nextSigners.sort((a, b) => a.order - b.order);

    if (nextSigners.length === 0) {
      return null;
    }

    return nextSigners[0];
  }, [signers]);

  const handleError = useCallback(() => {
    notifyError({
      message: UNSIGN_DOCUMENT_ERROR_MESSAGE,
    });
  }, [notifyError]);

  const handleConfirm = () => {
    const selectedDocumentId = documentId ? documentId : currentDocumentId;
    const additionalMessage = optionalMessageInputRef.current?.value;
    setIsSending(true);
    remindCurrentSigner({ documentId: selectedDocumentId!, additionalMessage })(apolloClient)
      .then(() => {
        notifySuccess({
          message: {
            id: 'phrase.remind-signing.success',
            defaultMessage: 'Reminder email sent to {email}',
            values: { email: nextSigner?.email },
          },
        });
      })
      .catch(() => {
        handleError();
      })
      .finally(() => {
        setIsSending(false);
        onHide();
      });
  };

  return (
    <SendReminderConfirmationModal
      handleConfirm={handleConfirm}
      show={show}
      onHide={onHide}
      isSending={isSending}
      nextSigner={nextSigner}
      optionalMessageInputRef={optionalMessageInputRef}
    />
  );
};
